import { Dispatch, MutableRefObject, SetStateAction, useCallback } from "react";
import { logger } from "../../../utils/logger";
import { fromPerpMarginMode } from "../../../utils/margin";
import { ethers, BigNumber } from "ethers";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { getCurrentChainConfig } from "../../../config/chains";
import { getGaslessPerpRouter, getPerpDomainName } from "../../../utils/gaslessDomains";
import { InviteStatusResponse } from "../invite/useInviteStatus";
import { getWeb3ProviderForChain } from "../../../utils/wallet/activeSigner";
import { IPerpOrderParams } from "./usePerpOrder.types";
import {
  PERP_ENGINE_ABI,
  PERP_OPEN_GAS_FLOOR,
  alignRawPriceToStep,
  floorPerpSizeToStep,
  parsePerpUnits,
  probeOpenPositionRevert,
  toPerpTransactionError,
} from "./usePerpOrder.helpers";
import { relayGaslessOpen, relayGaslessReduceOnly } from "./usePerpOrder.gasless";
import { marketAcceptablePrice } from "./usePerpOrder.bounds";
import { TradeExecutedDetail } from "../../../utils/tradeEvents";

interface UsePlacePerpOrderArgs {
  account: string | null | undefined;
  masterAccount: string | null | undefined;
  inviteStatus: InviteStatusResponse | undefined;
  /** The trader's max-slippage setting, basis points. */
  slippageBps: number;
  walletTransactionInFlightRef: MutableRefObject<boolean>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  revalidatePerpDataSoon: (detail?: TradeExecutedDetail) => void;
}

export const usePlacePerpOrder = ({
  account,
  masterAccount,
  inviteStatus,
  slippageBps,
  walletTransactionInFlightRef,
  setIsLoading,
  revalidatePerpDataSoon,
}: UsePlacePerpOrderArgs) =>
  useCallback(
    async (params: IPerpOrderParams): Promise<{ order_id: string; tx_hash: string }> => {
      if (!account) throw new Error("Not connected");
      if (!inviteStatus) {
        throw new Error("Checking invite access. Please try again in a moment.");
      }
      if (inviteStatus?.invite_required && !inviteStatus.allowed) {
        throw new Error("Invite code required to trade");
      }
      if (walletTransactionInFlightRef.current) {
        throw new Error(
          "Transaction already in progress. Please confirm or reject the open wallet request."
        );
      }

      const config = getCurrentChainConfig();
      // Use perpRouter (CLOBPerpetualRouter) on modular chains, fall back to orderHandler (PerpEngine) on consolidated chains
      const perpEngine = config.contracts.perpetualDex?.perpRouter || config.contracts.perpetualDex?.orderHandler;
      const gaslessPerpRouter = getGaslessPerpRouter(config);
      const perpDomainName = getPerpDomainName(config);
      const usdcAddress = config.contracts.perpetualDex?.usdc || config.contracts.erc20?.usdc;

      if (!perpEngine) throw new Error("PerpEngine not configured");
      if (!usdcAddress) throw new Error("USDC not configured");

      walletTransactionInFlightRef.current = true;
      setIsLoading(true);
      try {
        // Asserts the wallet is on this market's chain before anything is
        // signed. Without the check the app would happily sign for whatever
        // network the wallet was on — the contract then reverts in ways a trader
        // cannot interpret (NotTraderOrOperator, or a view call that "cannot
        // read market lot size"). `WrongNetworkError` names both chains so the
        // UI can offer a switch.
        const provider = await getWeb3ProviderForChain(config);
        const signer = provider.getSigner();

        // Convert display values to on-chain format
        const isMarketOrder = params.order_type === OrderTypeResponse.Market;
        const isLong = params.side === SideResponse.Buy;

        // Price: 18 decimals (e.g., "70000" → 70000 * 10^18)
        const typedPrice = isMarketOrder
          ? BigNumber.from(0)
          : parsePerpUnits(params.price || "0", "price");

        // Size: 18 decimals (e.g., "0.01" → 0.01 * 10^18)
        const rawSizeRaw = parsePerpUnits(params.size, "size");
        logger.debug("[PerpOrder][step] received size:", params.size, "→ rawSizeRaw:", rawSizeRaw.toString());

        // Floor size to market's amountStep — DataStore.setMarketContractSpecs
        // enforces (size % amountStep == 0) and reverts SizeNotOnStep otherwise.
        // We READ the live step from DataStore and refuse to submit if we can't
        // verify alignment — falling back to a raw size would re-create the bug
        // this code path exists to prevent.
        const { size: rawSize, priceStep } = await floorPerpSizeToStep(
          config,
          params,
          rawSizeRaw,
          provider
        );
        const rawPrice = isMarketOrder
          ? typedPrice
          : alignRawPriceToStep(typedPrice, priceStep, isLong);
        if (!rawPrice.eq(typedPrice)) {
          logger.debug("[PerpOrder][step] aligned price to priceStep", {
            requested: typedPrice.toString(),
            aligned: rawPrice.toString(),
            priceStep: priceStep?.toString(),
          });
        }

        // Acceptable price. A market order is bounded by the trader's max
        // slippage around the live touch (see usePerpOrder.bounds); it used to
        // send MaxUint256 / 0, i.e. "fill me at any price". Read before anything
        // is signed, so an unreadable book stops the order here.
        let acceptablePrice: BigNumber;
        if (isMarketOrder) {
          acceptablePrice = await marketAcceptablePrice({
            config,
            market: params.market,
            isBuy: isLong,
            slippageBps: params.slippageBps ?? slippageBps,
            priceStep,
          });
        } else {
          // Limit orders keep their legacy band around the limit price; the
          // limit itself is the trader's price protection there.
          const slippage = params.slippagePercent || 5;
          acceptablePrice = isLong
            ? rawPrice.mul(100 + slippage).div(100)
            : rawPrice.mul(100 - slippage).div(100);
        }

        // No separate deposit needed — BalanceManagerVault automatically
        // allocates collateral from CLOBEngine balance during placeOrder.
        // User must have deposited USDC to CLOBEngine via the onboarding flow.

        // Place order via openPosition (user-callable, no adapter auth needed)
        // PerpEngine.openPosition calculates margin from size/leverage and
        // allocates collateral from CLOBEngine balance via BalanceManagerVault
        const perpContract = new ethers.Contract(perpEngine, PERP_ENGINE_ABI, signer);
        // Contract validates the per-market max leverage. Keep only the uint16-safe
        // metadata ceiling here to avoid malformed values reaching the transaction.
        const rawLeverage = Number(params.leverage);
        const leverage = Number.isFinite(rawLeverage)
          ? Math.max(1, Math.min(Math.round(rawLeverage), 100))
          : 1;
        const marginMode = params.marginMode ?? 1; // default CROSS
        const orderType = isMarketOrder ? 1 : 0; // 0=LIMIT, 1=MARKET

        // ── Gasless open path (see relayGaslessOpen). reduceOnly has its own branch below. ──
        if (config.gasless && config.relayerUrl && gaslessPerpRouter && !params.reduceOnly) {
          return await relayGaslessOpen({
            account,
            masterAccount,
            params,
            rawPrice,
            rawSize,
            isLong,
            orderType,
            leverage,
            marginMode,
            acceptablePrice,
            chainId: config.chainId,
            perpDomainName,
            gaslessPerpRouter,
            relayerUrl: config.relayerUrl,
            perpEngine,
            agentRegistry: config.contracts.perpetualDex?.agentRegistry,
            signer,
            revalidatePerpDataSoon,
          });
        }

        // ── Gasless reduce-only path (see relayGaslessReduceOnly). ──
        if (config.gasless && config.relayerUrl && gaslessPerpRouter && params.reduceOnly) {
          return await relayGaslessReduceOnly({
            account,
            masterAccount,
            params,
            rawPrice,
            rawSize,
            isLong,
            orderType,
            marginMode,
            acceptablePrice,
            chainId: config.chainId,
            perpDomainName,
            gaslessPerpRouter,
            relayerUrl: config.relayerUrl,
            perpEngine,
            agentRegistry: config.contracts.perpetualDex?.agentRegistry,
            signer,
            revalidatePerpDataSoon,
          });
        }

        logger.debug("[PerpOrder] Opening position...", {
          market: params.market,
          side: isLong ? "LONG" : "SHORT",
          type: isMarketOrder ? "MARKET" : "LIMIT",
          price: ethers.utils.formatUnits(rawPrice, 18),
          size: ethers.utils.formatUnits(rawSize, 18),
          leverage,
          marginMode: marginMode === 1 ? "CROSS" : "ISOLATED",
          reduceOnly: params.reduceOnly ?? false,
        });

        const openPositionArgs = [
          account,           // trader
          params.market,     // market token address
          rawSize,           // size in 18 decimals
          rawPrice,          // price in 18 decimals (0 for market)
          isLong,            // true=LONG, false=SHORT
          marginMode,        // 0=ISOLATED, 1=CROSS
          leverage,          // e.g., 5
          orderType,         // 0=LIMIT, 1=MARKET
          acceptablePrice,   // slippage protection
        ] as const;

        if (params.reduceOnly) {
          const reduceOnlyArgs = [
            account,
            params.market,
            rawSize,
            rawPrice,
            isLong,
            marginMode,
            orderType,
            acceptablePrice,
          ] as const;

          try {
            await perpContract.callStatic.placeReduceOnlyOrder(
              ...reduceOnlyArgs
            );
          } catch (simErr: any) {
            throw toPerpTransactionError(
              simErr,
              "Failed to place reduce-only perp order"
            );
          }

          let gasLimit = PERP_OPEN_GAS_FLOOR;
          try {
            const estimatedGas =
              await perpContract.estimateGas.placeReduceOnlyOrder(
                ...reduceOnlyArgs
              );
            const buffered = estimatedGas.mul(150).div(100);
            if (buffered.gt(gasLimit)) gasLimit = buffered;
          } catch {
            // estimateGas can fail on some wallet RPCs; keep the safe floor.
          }

          const tx = await perpContract.placeReduceOnlyOrder(
            ...reduceOnlyArgs,
            { gasLimit }
          );
          const receipt = await tx.wait(1);
          const orderId =
            receipt.events?.[0]?.args?.orderId?.toString() ||
            receipt.transactionHash;

          logger.debug(
            `[PerpOrder] Reduce-only order placed: orderId=${orderId}, tx=${tx.hash}`
          );
          // No `openedPosition`: a reduce-only order does not open anything,
          // and its `side` is the OPPOSITE of the position it reduces (a long is
          // reduced by a sell). Reporting it as an open released the pending
          // close of a real position on that market and the order side, putting
          // a row the trader had already closed back on screen.
          revalidatePerpDataSoon();

          return {
            order_id: orderId,
            tx_hash: tx.hash,
          };
        }

        try {
          await perpContract.callStatic.openPosition(...openPositionArgs);
        } catch (simErr: any) {
          // The wallet's RPC frequently omits the revert returndata, leaving only
          // the request CALLDATA in the error — which surfaces as the bare
          // openPosition selector (0x51359a95) instead of the real reason. Re-probe
          // the revert through the app's own RPC (which returns the custom-error
          // data) so we can show the actual cause, e.g. "Insufficient margin balance".
          const probed = await probeOpenPositionRevert(
            perpEngine,
            openPositionArgs as unknown as any[]
          ).catch(() => undefined);
          throw toPerpTransactionError(probed ?? simErr, "Failed to place perp order");
        }

        // Size the gas limit dynamically. A flat 2M is enough for a resting
        // limit order, but a limit order that CROSSES the book and opens the
        // account's FIRST position does cold storage writes for both taker and
        // maker (position state + OI + funding + fees) and can exceed 2M,
        // OOG-reverting after the match already emitted. Estimate + 50% buffer,
        // with an 8M fallback for wallet/RPCs that cannot estimate Monad matches.
        let gasLimit = PERP_OPEN_GAS_FLOOR;
        try {
          const estimatedGas = await perpContract.estimateGas.openPosition(
            ...openPositionArgs
          );
          const buffered = estimatedGas.mul(150).div(100);
          if (buffered.gt(gasLimit)) gasLimit = buffered;
        } catch {
          // estimateGas can fail on some wallet RPCs; keep the Monad-safe floor.
        }
        const tx = await perpContract.openPosition(...openPositionArgs, {
          gasLimit,
        });
        const receipt = await tx.wait(1);

        // Extract orderId from events
        const orderId = receipt.events?.[0]?.args?.orderId?.toString() || receipt.transactionHash;

        logger.debug(`[PerpOrder] Order placed: orderId=${orderId}, tx=${tx.hash}`);
        revalidatePerpDataSoon({
          openedPosition: {
            instrument_id: params.market,
            side: params.side,
            // The margin mode MUST travel with the open. Without it the release
            // matched both legs, so adding to a cross position released the
            // pending close of an isolated one on the same market and side, and
            // the closed row came back as if the trader still held it.
            margin_type: fromPerpMarginMode(params.marginMode),
          },
        });

        return {
          order_id: orderId,
          tx_hash: tx.hash,
        };
      } catch (error: any) {
        throw toPerpTransactionError(error, "Failed to place perp order");
      } finally {
        walletTransactionInFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [account, masterAccount, inviteStatus, revalidatePerpDataSoon, slippageBps]
  );
